import { TableDrawer } from "@components/TableDrawer/TableDrawer";
import { useQuery } from "@hooks/useQuery";
import { decodeHTML } from "@utils/CommonUtils";
import { Pagination, TableColumnsType, Typography } from "antd";
import { get as _get } from "lodash";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { useNotification } from "~/Hooks/useNotification";
import { corporateCMSApi, post } from "~/Services";
import { corporateHost } from "~/Utils";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import { renderActionsColumn, renderDateColumn, renderPublishedColumn } from "~/Utils/TableRenderFunction";
import { drilldownDataCorporate } from "~/Utils/drilldownDataDum";
import { useLocalClients } from "../OurClients.hooks";

export const LocalClients = () => {
  const query = useQuery();
  const navigate = useNavigate();

  const { openToast } = useNotification();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(selectedRowData?.nid);
  const [pageSize, setPageSize] = useState<number>(10);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const [data, setData] = useState<any>([]);
  const [sortedData, setSortedData] = useState<any>([]);
  // const currentPageData = sortedData.slice(currentPage * 10, (currentPage + 1) * 10);
  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string | undefined>();
  const [drilldownData, setDrilldownData] = useState<any>(null);
  const [selectedPublished, setSelectedPublished] = useState<any>("");
  const publishedData: TypeData[] = [
    {
      label: "Yes",
      value: true,
    },
    {
      label: "No",
      value: false,
    },
  ];

  useEffect(() => {
    const fetchDrilldownData = async () => {
      const data = await drilldownDataCorporate();
      setDrilldownData(data);
    };

    fetchDrilldownData();
  }, []);

  const {
    data: drilldown,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchDocument,
  } = useLocalClients(id || "");

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    const filteredData = data.filter((item: any) => {
      const searchLower = decodeHTML(searchValue).toLowerCase();
      return decodeHTML(item.title).toLowerCase().includes(searchLower);
    });
    setSortedData(filteredData);
  }, [data, searchValue]);
  useEffect(() => {
    let filteredData = [...data];
    if (selectedPublished !== "") {
      const isPublished = selectedPublished === true;
      filteredData = filteredData.filter((item: any) => item.published === isPublished);
    }

    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [selectedPublished, data]);

  const getRecords = () => {
    setIsLoading(true);
    let url = `/api/local-client-details/all-fields`;
    corporateCMSApi
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          let envelopes = res.data;
          setTotalRecords(envelopes.length);
          setData(envelopes);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setData([]);
        setIsLoading(false);
      });
  };

  const columns: TableColumnsType = [
    {
      title: "Order Sequence",
      dataIndex: "field_local_order_sequence",
      key: "field_local_order_sequence",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_local_order_sequence"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_local_order_sequence"
            ? "descend"
            : undefined,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "title"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "title"
            ? "descend"
            : undefined,
    },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      render: renderPublishedColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "published"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "published"
            ? "descend"
            : undefined,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (value: any) => renderDateColumn(value),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "created_at"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "created_at"
            ? "descend"
            : undefined,
    },
    {
      title: "Last Update",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (value: any, record: any) => {
        if (value && record.created_at && value === record.created_at) {
          return "-";
        }
        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updated_at"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updated_at"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_: any, record: any) => {
        return renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/dedicated/corporate/training-academy/local-client/add?editId=${record?.id}`);
          },
          undefined,
          setDeleteModalVisible,
          "users"
        );
      },
    },
  ];

  useEffect(() => {
    getRecords();
  }, []);

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";

    if (sortOrder === "asc") {
      setSortOrder("desc");
      order = "desc";
    } else {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);

    const tempData = [...sortedData];
    setSortedData(tempData.sort((a: any, b: any) => handleSort(a, b, order, column.dataIndex)));
    setCurrentPage(0);
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (apiUrl === "") return;

    const payload = {
      action: action,
      method: method,
      module: "Training Academy",
      subModule: "ourClients_localClients",
      type: "DEDICATED",
      application: "MAHB_CORPORATE",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "MAHB_CORPORATE",
          apiUrl: apiUrl,
        },
      ],
      moduleKey: "MAHB_CORPORATE_Malaysia Airport Academy_Our Clients",
      payload: data,
      status: status,
    };

    post("/management/publish", payload)
      .then((response: any) => {
        openToast({
          content: "Data Sent for Review",
          type: "success",
        });
        if (response.status === "OK" && status == "PENDING") {
          navigate("/dedicated/status-information?activeTab=pending");
        } else {
          navigate("/dedicated/status-information?activeTab=draft");
        }
      })
      .catch((err: any) => {
        openToast({
          content: "Failed to send data",
          type: "error",
        });
      });
  };

  async function sendDeleteClient(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${corporateHost}/api/local-client/${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch (err) {}
  }
  const handlePublishedChange = (value: any) => {
    setSelectedPublished(value);
    setCurrentPage(0);
  };
  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={
          currentPageData
            ? currentPageData?.map((d: any) => {
                return {
                  ...d,
                  title: decodeHTML(d.title),
                };
              })
            : []
        }
        isLoading={isLoading}
        exportFileName="CONTRACT"
        isPagination={true}
        recordsPerPage={10}
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        hideExport={true}
        isServerSearchApi={false}
        searchPlaceholderText={"Search by Title"}
        onClick={(record) => {
          setSelectedRowData(record);
        }}
        additionalCTA={
          <div className="flex items-center space-x-2">
            <ActionDropdown
              value={selectedPublished}
              options={publishedData}
              onChange={handlePublishedChange}
              placeholder="Select Published"
              className="w-48"
            />
          </div>
        }
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          total={totalRecords}
          pageSize={pageSize}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage - 1);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
          // onChange={(curPage) => {
          //   setCurrentPage(curPage - 1);
          // }}
        />
      </div>

      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={_get(drilldownData, _get(drilldown, "subModule", "milestonesOverview"))}
        data={drilldown}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteClient}
        defaultReviewNotes="Kindly review this Malaysia Airports Academy"
      />
    </>
  );
};

export function handleSort(a: any, b: any, order: string, sortBy: string) {
  if (sortBy === "field_faqs_question") {
    const numberA = extractNumber(a[sortBy]);
    const numberB = extractNumber(b[sortBy]);

    if (order === "desc") {
      return numberA < numberB ? 1 : -1;
    } else if (order === "asc") {
      return numberA > numberB ? 1 : -1;
    } else {
      return 0;
    }
  }
  if (sortBy === "field_heading") {
    const valueA = a[sortBy] ? String(a[sortBy]).toLowerCase() : "";
    const valueB = b[sortBy] ? String(b[sortBy]).toLowerCase() : "";

    if (order === "desc") {
      return valueA < valueB ? 1 : -1;
    } else if (order === "asc") {
      return valueA > valueB ? 1 : -1;
    } else {
      return 0;
    }
  }

  if (order === "desc") {
    return a[sortBy] < b[sortBy] ? 1 : -1;
  } else if (order === "asc") {
    return a[sortBy] > b[sortBy] ? 1 : -1;
  } else {
    return 0;
  }
}

export function extractNumber(question: string): number {
  const match = question.match(/Q(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}
